<!-- \src\components\PricingAccordion\ManageTimeWindowModal.vue -->
<template>
  <div
    class="modal fade"
    id="manageTimeWindowModalLabel"
    tabindex="-1"
    aria-labelledby="manageTimeWindowModalLabel"
    aria-hidden="true"
    :ref="(el) => $emit('set-modal-ref', el)"
  >
    <div class="modal-dialog modal-dialog-centered wider-modal">
      <div class="modal-content text-start">
        <div class="modal-header">
          <h5 class="modal-title" id="manageTimeWindowModalLabel">
            {{ modalTitle }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form>
            <!-- Time Window Description -->
            <div class="form-group">
              <label for="timeWindowDescription"
                >Time Window Description (optional)</label
              >
              <input
                type="text"
                class="form-control"
                id="timeWindowDescription"
                placeholder="Enter description"
                v-model="timeWindowDescription"
                :disabled="operationType === 'delete'"
              />
            </div>

            <!-- Days of the Week -->
            <div class="form-group mt-3">
              <label>On the following days:</label><br />
              <div
                class="form-check form-check-inline"
                v-for="day in daysOfWeek"
                :key="day.name"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="day.name + 'Check'"
                  :value="day.value"
                  v-model="selectedDays"
                  :disabled="operationType === 'delete'"
                />
                <label class="form-check-label" :for="day.name + 'Check'">
                  {{ day.name }}
                </label>
              </div>
            </div>

            <!-- Time Range Selection -->
            <div class="form-group mt-3">
              <div class="row">
                <div class="col">
                  <label for="timeRange">Between:</label>
                  <select
                    class="form-control"
                    id="startTime"
                    v-model="startTime"
                    :disabled="operationType === 'delete'"
                  >
                    <option
                      v-for="time in timeSlots"
                      :key="time.value"
                      :value="time.value"
                    >
                      {{ time.label }}
                    </option>
                  </select>
                </div>
                <div class="col">
                  <label for="timeRange">And:</label>
                  <select
                    class="form-control"
                    id="endTime"
                    v-model="endTime"
                    :disabled="operationType === 'delete'"
                  >
                    <option
                      v-for="time in timeSlots"
                      :key="time.value"
                      :value="time.value"
                    >
                      {{ time.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Display Value -->
            <div
              v-if="currentStrategy.isAdjustByUtilization"
              class="form-group mt-3"
            >
              <UtilizationPricingSettings
                :ref="(el) => { $emit('update:utilizationPriceSettingsRef', el as ComponentPublicInstance | null) }"
                :initialData="utilizationData"
                :isAllDay="true"
                :isAllWeek="false"
                :priceWindowId="priceWindowId"
                :isDisabled="operationType === 'delete'"
              />
            </div>
            <div v-else class="col-md-12">
              <div
                class="p-3 d-flex justify-content-end align-items-center price-display"
              >
                <span>Display at</span>
                <PriceUnitComponent
                  :ref="(el) => { $emit('update:utilizationPriceSettingsRef', el as ComponentPublicInstance | null) }"
                  :value="getUtilizationPrice()"
                  :isPercent="getUsePercent()"
                  @update:value="updateUtilizationPrice($event)"
                  @update:isPercent="updateUsePercent($event)"
                />
                <i
                  class="bi bi-info-circle"
                  v-tooltip="{
                    content:
                      'When applying this strategy:<br>If % is selected, a booking channel\'s pricing will be shown as this percentage of the regular rate.<br>If $ is selected, this amount will be shown.',
                    placement: 'top',
                    triggers: ['hover', 'focus'],
                    html: true,
                    popperClass: 'custom-tooltip',
                  }"
                  tabindex="0"
                  role="button"
                  aria-label="Info"
                ></i>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer text-start">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="handleConfirm">
            {{ modalActionText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  PropType,
  ref,
  Ref,
  watch,
  ComponentPublicInstance,
} from 'vue';
import { DayOfWeekItem } from '@/models';
import { CalendarOptions } from '@fullcalendar/core';
import { useToast } from 'vue-toastification';
import { Modal } from 'bootstrap';
import {
  DailyUtilization,
  UtilizationPrice,
  TimeWindow,
  Strategy,
} from '@/api';
import PriceUnitComponent from '../PriceUnitComponent.vue';
import UtilizationPricingSettings from '../UtilizationPricingSettings.vue';
import { dynPricingDayOfWeek, fullCalendarDayOfWeek } from '@/utils/dateUtils';

export default defineComponent({
  components: { PriceUnitComponent, UtilizationPricingSettings },
  name: 'ManageTimeWindowModal',
  emits: [
    'manage-time-window-result',
    'set-modal-ref',
    'update:utilizationPriceSettingsRef',
  ],

  props: {
    calendarOptions: {
      type: Object as PropType<CalendarOptions>,
      required: true,
    },
    initialData: {
      type: Object as PropType<any>,
      required: false,
      default: () => ({}),
    },
    operationType: {
      type: String,
      required: true,
    },
    manageTimeWindowModalRef: {
      type: Object as PropType<HTMLElement | null>,
      required: true,
    },
    utilizationPriceSettingsRef: {
      type: Object as PropType<ComponentPublicInstance | undefined | null>,
      required: false,
    },
    currentStrategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const daysOfWeek = inject<Ref<DayOfWeekItem[]> | null>('daysOfWeek', null);
    const selectedDays = ref<number[]>([]);
    const timeWindowDescription = ref('');
    const startTime = ref(props.initialData.startTime ?? '');
    const endTime = ref(props.initialData.endTime ?? '');
    const range = ref<UtilizationPrice>({
      priceAmount: 100,
      usePercent: false,
      pricePercentage: null,
    });

    const toast = useToast();

    const utilizationData = ref<UtilizationPrice[]>([]);
    const priceWindowId = ref<number>(props.initialData?.id ?? 0);

    watch(
      () => props.initialData,
      (newData) => {
        // Existing code...
        priceWindowId.value = Number(newData.id) || 0;
        if (newData.extendedProps.utilizationRanges) {
          utilizationData.value = newData.extendedProps.utilizationRanges;
        } else {
          utilizationData.value = [getDefaultUtilizationRange()];
        }
      }
    );

    const resetForm = () => {
      selectedDays.value = [];
      timeWindowDescription.value = '';
      startTime.value = '';
      endTime.value = '';
    };

    // Helper function to generate the default utilization range
    const getDefaultUtilizationRange = (rangeValue = 0) => {
      return {
        low: 0,
        high: 100,
        pricePercentage: rangeValue ?? 0,
        priceAmount: 0,
        id: 0,
        usePercent: true,
      };
    };

    const generateTimeSlots = (
      slotDuration: string,
      slotMinTime: string,
      slotMaxTime: string
    ) => {
      const slots = [];
      const [minHours, minMinutes] = slotMinTime.split(':').map(Number);
      const [maxHours, maxMinutes] = slotMaxTime.split(':').map(Number);
      const [durationHours, durationMinutes] = slotDuration
        .split(':')
        .map(Number);

      let current = new Date();
      current.setHours(minHours, minMinutes, 0, 0);

      const end = new Date();
      end.setHours(maxHours, maxMinutes, 0, 0);

      while (current <= end) {
        slots.push({
          label: current.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          value: `${
            current.getHours() < 10
              ? '0' + current.getHours()
              : current.getHours()
          }:${
            current.getMinutes() < 10
              ? '0' + current.getMinutes()
              : current.getMinutes()
          }:00`,
        });
        current.setHours(current.getHours() + durationHours);
        current.setMinutes(current.getMinutes() + durationMinutes);
      }

      return slots;
    };

    const timeToMinutes = (s: string) => {
      if (!s) return 0;
      const parts = s.split(':').map(Number);
      return (parts[0] ?? 0) * 60 + (parts[1] ?? 0) + (parts[2] ?? 0) / 60;
    };

    const findClosestSlot = (
      slots: { value: string }[],
      timeStr: string,
      preferEnd: boolean
    ): string => {
      if (!slots.length) return '';
      if (!timeStr)
        return preferEnd ? slots[slots.length - 1].value : slots[0].value;
      const timeMins = timeToMinutes(timeStr);
      const withMins = slots.map((s) => ({
        value: s.value,
        mins: timeToMinutes(s.value),
      }));
      if (preferEnd) {
        const valid = withMins.filter((s) => s.mins <= timeMins);
        return (
          valid.length ? valid[valid.length - 1] : withMins[withMins.length - 1]
        ).value;
      } else {
        const valid = withMins.filter((s) => s.mins >= timeMins);
        return (valid.length ? valid[0] : withMins[0]).value;
      }
    };

    const verifyTimeWindow = () => {
      if (daysOfWeek?.value?.length === 0) {
        toast.error('You must selected at least one day of the week.');
        return false;
      }

      if (!timeSlots.find((timeSlot) => timeSlot.value === startTime.value)) {
        toast.error('You must enter a valid start time.');
        return false;
      }

      if (!timeSlots.find((timeSlot) => timeSlot.value === endTime.value)) {
        toast.error('You must enter a valid end time.');
        return false;
      }

      if (startTime.value >= endTime.value) {
        toast.error('End time must be later than Start time.');
        return false;
      }

      const errors = document.querySelector('.form-control-error');
      if (errors) {
        toast.error(
          'Cannot save time window due to validation errors. Please check each utilization range.'
        );
        return false;
      }

      return true;
    };

    const closeModal = () => {
      const modalElement = props.manageTimeWindowModalRef;
      if (modalElement) {
        const bootstrapModal =
          Modal.getInstance(modalElement) || new Modal(modalElement);
        bootstrapModal.hide();
      }
    };

    const handleConfirm = () => {
      const isResultValid = verifyTimeWindow();
      if (!isResultValid) {
        return;
      }

      const result = {
        events: [] as DailyUtilization[],
        operationType: props.operationType || 'create',
      };

      let utilizationRanges: any;
      let defaultUtilizationRange =
        utilizationData.value?.[0] ?? getDefaultUtilizationRange();
      if (props.currentStrategy.isAdjustByUtilization) {
        // Get the utilization ranges from the component's exposed data
        if (
          props.utilizationPriceSettingsRef &&
          'utilizationData' in props.utilizationPriceSettingsRef
        ) {
          const componentData = props.utilizationPriceSettingsRef
            .utilizationData as DailyUtilization[];
          utilizationRanges = componentData[0]?.timeWindows?.[0]
            ?.utilizationRanges || [defaultUtilizationRange];
        } else {
          utilizationRanges = [defaultUtilizationRange];
        }
      } else {
        utilizationRanges =
          utilizationData.value.length > 0
            ? utilizationData.value
            : [defaultUtilizationRange];
      }

      selectedDays.value.forEach((day) => {
        const event: DailyUtilization = {
          dayOfWeek: fullCalendarDayOfWeek(day),
          timeWindows: [],
        };

        const timeWindows: TimeWindow[] = [
          {
            timeWindowDescription: timeWindowDescription.value,
            startTime: startTime.value,
            endTime: endTime.value,
            utilizationRanges: [],
          },
        ];

        timeWindows[0].utilizationRanges = utilizationRanges;
        event.timeWindows = timeWindows;

        result.events.push(event);
      });

      console.log('result:', result);

      emit('manage-time-window-result', result);
      resetForm();
      closeModal();
    };

    const getPriceValue = (range: UtilizationPrice): number => {
      return range.usePercent
        ? range.pricePercentage ?? 100
        : range.priceAmount ?? 0;
    };

    const getUsePercent = () => {
      const utilizationRange = utilizationData.value[0];
      return utilizationRange?.usePercent ?? false;
    };

    const updateUtilizationPrice = (newValue: number) => {
      const utilizationRange = utilizationData.value[0];
      if (utilizationRange) {
        if (utilizationRange.usePercent) {
          utilizationRange.pricePercentage = newValue;
        } else {
          utilizationRange.priceAmount = newValue;
        }
      }
    };

    const updateUsePercent = (newUsePercent: boolean) => {
      const utilizationRange = utilizationData.value[0];
      utilizationRange.usePercent = newUsePercent;

      // When switching between percentage and amount, we need to ensure the correct property is set
      if (newUsePercent) {
        utilizationRange.pricePercentage = utilizationRange.priceAmount ?? 0;
        utilizationRange.priceAmount = null;
      } else {
        utilizationRange.priceAmount = utilizationRange.pricePercentage ?? 0;
        utilizationRange.pricePercentage = 100;
      }
    };

    const getUtilizationPrice = () => {
      const utilizationRange = utilizationData.value[0];
      if (!utilizationRange) return 0;

      return utilizationRange.usePercent
        ? utilizationRange.pricePercentage ?? 0
        : utilizationRange.priceAmount ?? 0;
    };

    const updatePriceValue = (range: UtilizationPrice, newValue: number) => {
      if (range.usePercent) {
        if (newValue > 100) {
          toast.warning(
            'Setting a percentage over 100% will increase the price of the tee time.'
          );
        }
        range.pricePercentage = newValue;
        range.priceAmount = null;
      } else {
        range.priceAmount = newValue;
        range.pricePercentage = null;
      }
    };

    const timeSlots = generateTimeSlots(
      props.calendarOptions.slotDuration as string,
      props.calendarOptions.slotMinTime as string,
      props.calendarOptions.slotMaxTime as string
    );

    watch(
      () => props.initialData,
      (newData) => {
        const rawStart = newData.startTime ?? '';
        const rawEnd = newData.endTime ?? '';
        const startMatch = timeSlots.find((s) => s.value === rawStart);
        const endMatch = timeSlots.find((s) => s.value === rawEnd);
        startTime.value = startMatch
          ? rawStart
          : findClosestSlot(timeSlots, rawStart, false);
        endTime.value = endMatch
          ? rawEnd
          : findClosestSlot(timeSlots, rawEnd, true);
        timeWindowDescription.value = newData.title ?? '';

        selectedDays.value = [
          dynPricingDayOfWeek(newData.extendedProps.dayOfWeek),
        ];

        range.value = {
          priceAmount: newData.extendedProps.priceAmount,
          pricePercentage: newData.extendedProps.pricePercentage,
          usePercent: newData.extendedProps.usePercent ?? true,
        };
      }
    );

    const modalTitle = computed(() => {
      switch (props.operationType) {
        case 'edit':
          return 'Edit Time Window';
        case 'delete':
          return 'Delete Time Window';
        case 'copy':
          return 'Copy Time Window';
        case 'create':
        default:
          return 'Add Time Window';
      }
    });

    const modalActionText = computed(() => {
      switch (props.operationType) {
        case 'edit':
          return 'Edit';
        case 'delete':
          return 'Delete';
        case 'copy':
          return 'Copy';
        case 'create':
        default:
          return 'Add';
      }
    });

    return {
      daysOfWeek,
      timeSlots,
      selectedDays,
      timeWindowDescription,
      startTime,
      endTime,
      range,
      modalTitle,
      modalActionText,
      updateUtilizationPrice,
      updateUsePercent,
      getUtilizationPrice,
      getUsePercent,
      handleConfirm,
      getPriceValue,
      updatePriceValue,
      utilizationData,
      priceWindowId,
    };
  },
});
</script>
<style scoped>
.min-width-44 {
  min-width: 44px;
}

.price-unit {
  margin-left: 0px;
}

.wider-modal {
  max-width: 764px;
  width: 90%;
}
</style>
